import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../components/Logo';

export function RestoreStartScreen({ email, onStart }: { email?: string; onStart: () => Promise<void> }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm text-center">
        <div className="mb-6 flex justify-center">
          <Logo variant="mark" size={48} />
        </div>
        <h1 className="text-lg font-semibold tracking-tightish text-ink-primary">
          {t('iframe.restore.title')}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-secondary">{t('iframe.restore.body')}</p>
        {email && (
          <p className="mt-3 font-mono text-xs text-ink-tertiary">
            {t('iframe.restore.codeTo')} <span className="text-ink-primary">{email}</span>
          </p>
        )}
        <button
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onStart();
            } catch (e) {
              setError(e instanceof Error ? e.message : 'failed');
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-lg bg-bit2me px-4 text-sm font-medium text-white transition-colors hover:bg-bit2me-mid disabled:bg-ink-muted"
        >
          {busy ? t('iframe.common.working') : t('iframe.restore.cta')}
        </button>
        {error && <p className="mt-3 text-xs text-bad">{error}</p>}
      </div>
    </main>
  );
}

export function RestoreCodeScreen({
  email,
  onSubmit,
}: {
  email?: string;
  onSubmit: (code: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <Logo variant="mark" size={36} />
          </div>
          <h1 className="text-lg font-semibold tracking-tightish text-ink-primary">
            {t('iframe.restore.codeTitle')}
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-ink-secondary">
            {t('iframe.restore.codeBody', { email: email ?? '' })}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            autoComplete="one-time-code"
            required
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="block h-11 w-full rounded-lg border border-line bg-surface-white px-3 text-center font-mono text-base tracking-[0.3em] text-ink-primary placeholder:text-ink-muted focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
          />
          {error && <p className="text-xs text-bad">{error}</p>}
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-bit2me px-4 text-sm font-medium text-white transition-colors hover:bg-bit2me-mid disabled:bg-ink-muted"
          >
            {busy ? t('iframe.common.working') : t('iframe.restore.codeSubmit')}
          </button>
        </form>
      </div>
    </main>
  );
}
